// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Image,
  Message,
  Modal,
  Popconfirm,
  Space,
  Table,
  TableColumnProps,
  Upload
} from '@arco-design/web-react';
import dayjs from 'dayjs';

import { get, post } from '../requestNew';
import { baseUrl } from '../utils';
import { EventCalendarImageItem } from './types';
import './index.scss';

const { MonthPicker } = DatePicker;
const FormItem = Form.Item;
const URL_UPLOAD = baseUrl + '/common/uploadFile?bizDomain=event-calendar';

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 16 }
};

const getUploadUrl = (file: any) => file?.url || file?.response?.data || '';

const formatMonthValue = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}$/.test(value)) return value;
    const cnMatch = value.match(/^(\d{4})年(\d{1,2})月$/);
    if (cnMatch) {
      return `${cnMatch[1]}-${String(cnMatch[2]).padStart(2, '0')}`;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('YYYY-MM') : '';
  }
  if (typeof value.format === 'function') {
    return value.format('YYYY-MM');
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM') : '';
};

function EventCalendarConfig() {
  const [form] = Form.useForm<EventCalendarImageItem>();
  const [formSearch] = Form.useForm<EventCalendarImageItem>();
  const [visible, setVisible] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [pageNum, setPageNum] = useState(1);
  const [total, setTotal] = useState(0);
  const [dataList, setDataList] = useState<EventCalendarImageItem[]>([]);
  const [imageFileList, setImageFileList] = useState<any[]>([]);
  const [monthValue, setMonthValue] = useState('');

  const formRef = useRef<EventCalendarImageItem>({});

  const columns: TableColumnProps[] = [
    {
      title: '月份',
      dataIndex: 'month',
      width: 120,
      render: text => (text ? dayjs(text).format('YYYY年M月') : '-')
    },
    {
      title: '下载图预览',
      dataIndex: 'downloadImageUrl',
      width: 120,
      render: text => {
        return text ? <Image src={text} width={60} height={60} preview={true} style={{ borderRadius: 4 }} /> : '-';
      }
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      width: 180,
      render: text => (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-')
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record: EventCalendarImageItem) => (
        <>
          <Button className="btn" type="text" onClick={() => edit(record)}>
            编辑
          </Button>
          <Popconfirm focusLock title="提示" content="是否确认删除?" onOk={() => del(record)}>
            <Button className="btn" type="text" status="danger">
              删除
            </Button>
          </Popconfirm>
        </>
      )
    }
  ];

  const getList = (page?: { pageNum: number; pageSize: number }) => {
    const searchValues = formSearch.getFieldsValue();
    const params: Record<string, any> = {
      pageNum,
      pageSize,
      ...page
    };
    const searchMonth = formatMonthValue(searchValues.month);
    if (searchMonth) {
      params.month = searchMonth;
    }

    get<any>('/marketEvent/admin/calendarImage/page', params)
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          const { list, total: totalCount } = data;
          setTotal(totalCount);
          setDataList(list || []);
        } else {
          Message.error(message);
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      });
  };

  const search = () => {
    setPageNum(1);
    setPageSize(20);
    getList({ pageNum: 1, pageSize: 20 });
  };

  const add = () => {
    formRef.current = {};
    form.resetFields();
    setImageFileList([]);
    setMonthValue('');
    setVisible(true);
  };

  const del = (record: EventCalendarImageItem) => {
    post(`/marketEvent/admin/calendarImage/delete?id=${record.id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('删除成功！');
          getList();
        } else {
          Message.error(message);
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      });
  };

  const edit = (record: EventCalendarImageItem) => {
    formRef.current = record;
    form.resetFields();
    setImageFileList(
      record.downloadImageUrl
        ? [{ uid: `img-${record.id}`, url: record.downloadImageUrl, name: 'downloadImage' }]
        : []
    );
    setMonthValue(record.month || '');
    form.setFieldsValue({
      downloadImageUrl: record.downloadImageUrl
    });
    setVisible(true);
  };

  const editCancel = () => {
    formRef.current = {};
    setVisible(false);
    form.resetFields();
    setImageFileList([]);
    setMonthValue('');
  };

  const handleImageChange = (fileList: any[]) => {
    const latest = fileList.slice(-1);
    const latestUrl = getUploadUrl(latest[0]);
    setImageFileList(latest);
    form.setFieldValue('downloadImageUrl', latestUrl || '');
    if (latestUrl) {
      Message.success('上传成功');
    }
  };

  const onOk = () => {
    if (!monthValue || !/^\d{4}-\d{2}$/.test(monthValue)) {
      Message.error('请选择有效月份');
      return;
    }
    form.validate().then((values: EventCalendarImageItem) => {
      const data = {
        id: formRef.current?.id,
        month: monthValue,
        downloadImageUrl: values.downloadImageUrl
      };

      post('/marketEvent/admin/calendarImage/save', data)
        .then((res: any) => {
          const { code, message } = res || {};
          if (code === 200) {
            Message.success('保存成功！');
            getList();
            editCancel();
          } else {
            Message.error(message);
          }
        })
        .catch(() => {
          Message.error('网络错误!');
        });
    });
  };

  useEffect(() => {
    getList();
  }, [pageNum, pageSize]);

  return (
    <div className="event-calendar-config table-flex">
      <Space style={{ flexWrap: 'wrap', marginBottom: 16 }}>
        <Form style={{ width: '100%' }} autoComplete="off" layout="inline" form={formSearch}>
          <FormItem field="month">
            <MonthPicker
              format="YYYY年M月"
              style={{ width: 140 }}
              allowClear
              placeholder="筛选月份"
              onChange={(_, date) => {
                formSearch.setFieldValue('month', formatMonthValue(date) || undefined);
              }}
            />
          </FormItem>
          <FormItem>
            <Button type="primary" onClick={search}>
              查询
            </Button>
          </FormItem>
        </Form>
        <Button type="primary" onClick={add}>
          新增配置
        </Button>
      </Space>

      <Table
        columns={columns}
        data={dataList}
        pagination={{
          total,
          current: pageNum,
          pageSize,
          showTotal: true,
          onChange: (current, size) => {
            setPageNum(current);
            setPageSize(size);
          }
        }}
      />

      <Modal
        title={formRef.current?.id ? '编辑日历下载图' : '新增日历下载图'}
        visible={visible}
        onOk={onOk}
        onCancel={editCancel}
        autoFocus={false}
        focusLock={true}
      >
        <Form form={form} {...formItemLayout} autoComplete="off">
          <FormItem label="月份" required>
            <MonthPicker
              format="YYYY年M月"
              style={{ width: '100%' }}
              disabled={!!formRef.current?.id}
              placeholder="请选择月份"
              value={monthValue ? dayjs(`${monthValue}-01`) : undefined}
              onChange={(dateString, date) => {
                setMonthValue(formatMonthValue(date) || formatMonthValue(dateString));
              }}
            />
          </FormItem>
          <FormItem
            label="下载图片"
            field="downloadImageUrl"
            rules={[{ required: true, message: '请上传下载图片' }]}
          >
            <Upload
              action={URL_UPLOAD}
              headers={{ Authorization: localStorage.getItem('authToken') || '' }}
              listType="picture-card"
              limit={1}
              fileList={imageFileList}
              onChange={handleImageChange}
            />
          </FormItem>
        </Form>
      </Modal>
    </div>
  );
}

export default EventCalendarConfig;
